/**
 * Vista "Per prodotto" (#by-product) del Triage — workflow Ticket per-prodotto,
 * indipendente dal mondo SRP.
 *
 * Due sezioni in sequenza:
 *  1. "Triage da avviare" (status=open): i triage non ancora presi in carico, ordinati per
 *     rischio decrescente (#KEV → severità massima → #clienti impattati). Espandendo un
 *     prodotto si vedono le CVE aperte (ordinate KEV-first poi severità) e il pulsante
 *     "Avvia triage" che lo prende in carico (l'utente diventa triage lead).
 *  2. "Triage in corso" (status=in_triage): i triage presi in carico, stesso ordinamento.
 *     Il triage lead/collaboratore può editare lo stato VEX di ogni CVE, gestire i
 *     collaboratori (solo lead), chiudere il triage ("non impattato") o promuoverlo a bozza SRP (Actively Exploited Vulnerability o Severe Incident).
 *
 * Il pulsante "Mostra chiusi" (sotto "Triage in corso"; default: nascosti) aggiunge una terza
 * sezione "Ticket chiusi": i ticket chiusi senza escalation (status=closed) e quelli promossi a
 * report ENISA (status=promoted), con un badge che dice in breve perché sono stati chiusi
 * (vedi closeReason.ts). I ticket chiusi e mai promossi si possono riaprire ("Riapri": tornano
 * "da avviare"; il server notifica e registra l'audit). Le liste vengono caricate solo quando
 * la sezione è visibile.
 *
 * Le righe sono raggruppate per prodotto; i Ticket manuali (multi-release, creati dall'analista
 * con "Nuovo ticket") formano un gruppo a sé e mostrano titolo e release coinvolte. Sui Ticket
 * manuali l'analista assegna/rimuove le CVE con un typeahead sulle CVE note (CvePicker).
 * Lo stato di espansione è condiviso tra le due sezioni (per id) così un triage appena avviato
 * resta aperto quando migra di sezione.
 */

import { Fragment, useEffect, useRef, useState } from 'react';
import { ModalPortal } from '../../components/ModalPortal.js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, usePermissions } from '../../auth/index.js';
import { ApiError } from '../../api/client.js';
import {
  useTickets,
  useTicket,
  usePromoteTicket,
  useStartTicket,
  useLeaveTicket,
  useAddCollaborator,
  useRemoveCollaborator,
  useCloseTicket,
  useCustomerNoteSettings,
  useReopenTicket,
  useUpdateCveState,
  useUnassignTicketCve,
} from './hooks.js';
import { STATI_VEX_TRIAGE, type Ticket, type TicketCve, type TicketRelease } from './types.js';
import { TicketCreateDialog } from './TicketCreateDialog.js';
import { CvePicker } from './CvePicker.js';
import { NoteAnalistaForm, useNoteAnalista } from './NoteAnalista.js';
import { TicketAuditTrail } from './TicketAuditTrail.js';
import { TICKET_PARAM, ticketUrl } from './routes.js';
import { CopyButton } from '../../components/CopyButton.js';
import { SorgenteDettaglio, formattaDataOra } from './TicketSourceFields.js';
import { useProducts } from '../products/hooks.js';
import { REPORT_CATEGORY_LABEL, type ReportCategory } from '../srp/category.js';
import { ENISA_REPORTS_PATH, enisaReportPath } from '../srp/routes.js';
import { isRiapribile, motivoChiusura, type TipoMotivoChiusura } from './closeReason.js';
import { DEFAULT_CUSTOMER_NOTE_MIN_WORDS, countWords } from './customerNote.js';
import type { CraCategory } from '../products/types.js';
import styles from './Triage.module.css';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Classe CSS del badge in base alla severità (allineata a TriagePage). */
function classeSeverita(severity: string | undefined): string {
  const sev = (severity ?? '').toUpperCase();
  if (sev === 'CRITICAL') return styles.badgeCritical ?? '';
  if (sev === 'HIGH') return styles.badgeHigh ?? '';
  if (sev === 'MEDIUM') return styles.badgeMedium ?? '';
  if (sev === 'LOW') return styles.badgeLow ?? '';
  return styles.badgeInfo ?? '';
}

function messaggioErrore(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Errore sconosciuto';
}

/** Rango numerico della severità per ordinamento (più alto = più grave). */
/** Id del ticket dal parametro di deep link (`?ticket=<id>`): intero positivo, altrimenti undefined. */
function idTicketDaParametro(valore: string | null): number | undefined {
  if (!valore || !/^\d+$/.test(valore)) return undefined;
  const id = Number(valore);
  return id > 0 ? id : undefined;
}

function rangoSeverita(severity: string | undefined): number {
  switch ((severity ?? '').toUpperCase()) {
    case 'CRITICAL':
      return 5;
    case 'HIGH':
      return 4;
    case 'MEDIUM':
      return 3;
    case 'LOW':
      return 2;
    case 'INFO':
      return 1;
    default:
      return 0;
  }
}

/**
 * Rango numerico della categoria CRA per ordinamento decrescente
 * (più alto = più urgente): critical=2, important=1, default/undefined=0.
 */
function rangoCategoriaCra(cat: CraCategory | undefined): number {
  switch (cat) {
    case 'critical':
      return 2;
    case 'important':
      return 1;
    case 'default':
    default:
      return 0;
  }
}

/** Numero di CVE KEV (sfruttate note) del triage. */
function contaKev(t: Ticket): number {
  return t.payload.cves.filter((c) => c.knownExploited).length;
}

/** Severità massima (rango) tra le CVE del triage. */
function severitaMassima(t: Ticket): number {
  return t.payload.cves.reduce((max, c) => Math.max(max, rangoSeverita(c.severity)), 0);
}

/** Numero di clienti impattati dal triage. */
function contaClienti(t: Ticket): number {
  return t.payload.impactedCustomers.length;
}

/** Etichetta della severità massima (per il badge di riga). */
function etichettaSeveritaMassima(t: Ticket): string {
  const ordine = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO'];
  let best: string | undefined;
  for (const c of t.payload.cves) {
    const sev = (c.severity ?? '').toUpperCase();
    if (best === undefined || ordine.indexOf(sev) < ordine.indexOf(best)) {
      if (ordine.includes(sev)) best = sev;
    }
  }
  return best ?? 'N/D';
}

/**
 * Confronto rischio decrescente: #KEV, poi severità massima, poi #clienti impattati.
 * Restituisce <0 se `a` è più urgente di `b` (così `sort` mette i più urgenti per primi).
 */
function confrontaRischio(a: Ticket, b: Ticket): number {
  return (
    contaKev(b) - contaKev(a) ||
    severitaMassima(b) - severitaMassima(a) ||
    contaClienti(b) - contaClienti(a)
  );
}

/** Ordina le CVE: KEV prima, poi severità decrescente. */
function ordinaCve(cves: TicketCve[]): TicketCve[] {
  return [...cves].sort(
    (a, b) =>
      (b.knownExploited ? 1 : 0) - (a.knownExploited ? 1 : 0) ||
      rangoSeverita(b.severity) - rangoSeverita(a.severity),
  );
}

/** Normalizza un identificativo attore (trim + lowercase), come lato server. */
function normalizzaAttore(actor: string | undefined): string {
  return (actor ?? '').trim().toLowerCase();
}

/** Chiave del gruppo dei Ticket manuali (multi-release, non legati a un solo prodotto). */
const GRUPPO_MANUALI = 'Ticket manuali';

/** Release di un Ticket raggruppate per prodotto (es. ["Gateway 1.0, 2.0", "Sensor 3.1"]). */
function riepilogoRelease(t: Ticket): string[] {
  const perProdotto = new Map<string, string[]>();
  for (const r of t.releases) {
    const arr = perProdotto.get(r.productName) ?? [];
    arr.push(r.releaseVersion);
    perProdotto.set(r.productName, arr);
  }
  return [...perProdotto.entries()].map(([p, vs]) => `${p} ${vs.join(', ')}`);
}

/**
 * Raggruppa i triage per prodotto e ordina: i gruppi per categoria CRA decrescente
 * (critical first), poi per rischio del triage più urgente;
 * le righe entro il gruppo per rischio decrescente.
 */
/** Release di un ticket raggruppate per prodotto, nell'ordine di inserimento. */
function versioniPerProdotto(
  releases: TicketRelease[],
): Array<{ productInternalId: string; productName: string; versioni: string[] }> {
  const gruppi = new Map<
    string,
    { productInternalId: string; productName: string; versioni: string[] }
  >();
  for (const r of releases) {
    const g = gruppi.get(r.productInternalId) ?? {
      productInternalId: r.productInternalId,
      productName: r.productName,
      versioni: [],
    };
    g.versioni.push(r.releaseVersion);
    gruppi.set(r.productInternalId, g);
  }
  return [...gruppi.values()];
}

function raggruppaPerProdotto(
  triages: Ticket[],
  categoriaDi: (t: Ticket) => CraCategory | undefined = () => undefined,
): Array<[string, Ticket[]]> {
  const gruppi = new Map<string, Ticket[]>();
  for (const t of triages) {
    const key = t.origin === 'manual' ? GRUPPO_MANUALI : (t.productName ?? '—');
    const arr = gruppi.get(key) ?? [];
    arr.push(t);
    gruppi.set(key, arr);
  }
  for (const arr of gruppi.values()) arr.sort(confrontaRischio);
  return [...gruppi.entries()].sort(([, a], [, b]) => {
    const catA = rangoCategoriaCra(categoriaDi(a[0]!));
    const catB = rangoCategoriaCra(categoriaDi(b[0]!));
    return catB - catA || confrontaRischio(a[0]!, b[0]!);
  });
}

// ---------------------------------------------------------------------------
// Dialog: promozione a bozza SRP
// ---------------------------------------------------------------------------

/** Errore mostrato quando le note non salvate non si possono salvare prima di un'azione. */
const ERRORE_NOTE_NON_SALVATE =
  'Le note di analisi non sono state salvate (vedi il riquadro note): risolvi e riprova.';

function DialogPromuovi({
  triageId,
  numRelease,
  numCve,
  category,
  salvaNote,
  onChiudi,
}: {
  triageId: number;
  numRelease: number;
  numCve: number;
  category: ReportCategory;
  /** Salva le note non ancora salvate prima della promozione (false = non salvate). */
  salvaNote: () => Promise<boolean>;
  onChiudi: () => void;
}) {
  const etichetta = REPORT_CATEGORY_LABEL[category];
  const navigate = useNavigate();
  const { mutate, isPending, isError, error } = usePromoteTicket(triageId);
  const [erroreNote, setErroreNote] = useState(false);

  async function conferma() {
    setErroreNote(false);
    if (!(await salvaNote())) {
      setErroreNote(true);
      return;
    }
    mutate(category, {
      onSuccess: () => {
        onChiudi();
        navigate(ENISA_REPORTS_PATH);
      },
    });
  }

  return (
    <ModalPortal onClose={onChiudi}>
      <div
        className={styles.dialogOverlay}
        role="dialog"
        aria-modal="true"
        data-testid="dialog-promuovi"
      >
        <div className={styles.dialogCard}>
          <p className={styles.dialogTitle}>Promuovi a bozza SRP: {etichetta}</p>
          <p className={styles.dialogWarning} data-testid="avviso-promozione">
            Promuovere a bozza SRP report come <strong>{etichetta}</strong>? I timer interni SLA
            partiranno, le note di analisi del ticket verranno riportate sul report e
            amministratori, responsabili del prodotto e chi segue il ticket riceveranno una notifica
            email interna (nulla viene inviato a ENISA).
            {numRelease > 1 && (
              <>
                {' '}
                Verranno create <strong>{numRelease} bozze SRP</strong>, una per release.
              </>
            )}
          </p>
          {numCve === 0 && (
            <p className={styles.dialogWarning} role="alert" data-testid="avviso-senza-cve">
              ⚠ Nessuna CVE selezionata: il report verrà creato senza CVE. Potrai comunque procedere
              con le notifiche ENISA.
            </p>
          )}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={conferma}
              disabled={isPending}
              data-testid="btn-conferma-promozione"
            >
              {isPending ? 'Promozione…' : `Promuovi come ${etichetta}`}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onChiudi}
              disabled={isPending}
              data-testid="btn-annulla-promozione"
            >
              Annulla
            </button>
            {isError && (
              <span className={styles.inlineMutError} data-testid="errore-promozione">
                {messaggioErrore(error)}
              </span>
            )}
            {erroreNote && (
              <span className={styles.inlineMutError} data-testid="errore-promozione-note">
                {ERRORE_NOTE_NON_SALVATE}
              </span>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ---------------------------------------------------------------------------
// Dialog: chiusura anticipata (non impattato)
// ---------------------------------------------------------------------------

function DialogChiudi({
  triageId,
  notaCliente,
  salvaNote,
  onChiudi,
  onChiuso,
}: {
  triageId: number;
  /** Nota per il cliente scritta nel dialog delle azioni (solo ticket manuali). */
  notaCliente?: string;
  /** Salva le note non ancora salvate prima della chiusura (false = non salvate). */
  salvaNote: () => Promise<boolean>;
  onChiudi: () => void;
  /** Chiusura riuscita (dopo `onChiudi`): es. svuota la bozza della nota per il cliente. */
  onChiuso?: () => void;
}) {
  const [reason, setReason] = useState('Prodotto non impattato');
  const { mutate, isPending, isError, error } = useCloseTicket(triageId);
  const [erroreNote, setErroreNote] = useState(false);

  async function conferma() {
    if (!reason.trim()) return;
    setErroreNote(false);
    if (!(await salvaNote())) {
      setErroreNote(true);
      return;
    }
    const nota = notaCliente?.trim();
    mutate(
      { reason: reason.trim(), ...(nota ? { customerNote: nota } : {}) },
      {
        onSuccess: () => {
          onChiudi();
          onChiuso?.();
        },
      },
    );
  }

  return (
    <ModalPortal onClose={onChiudi}>
      <div
        className={styles.dialogOverlay}
        role="dialog"
        aria-modal="true"
        data-testid="dialog-chiudi"
      >
        <div className={styles.dialogCard}>
          <p className={styles.dialogTitle}>Chiudi il triage</p>
          <p className={styles.dialogWarning}>
            {notaCliente
              ? 'Chiusura senza escalation: il triage resta nel registro e non genera SRP; la nota per il cliente viene inviata a chi ha segnalato il ticket.'
              : 'Chiusura senza escalation: il triage resta nel registro ma non genera SRP né notifiche.'}{' '}
            Indica la motivazione (es. prodotto non impattato).
          </p>
          <div className={styles.formGroup}>
            <textarea
              className={styles.formTextarea}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivazione della chiusura…"
              data-testid={`textarea-motivo-chiusura-${triageId}`}
            />
          </div>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={conferma}
              disabled={isPending || !reason.trim()}
              data-testid={`btn-conferma-chiusura-${triageId}`}
            >
              {isPending ? 'Chiusura…' : 'Chiudi triage'}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onChiudi}
              disabled={isPending}
              data-testid={`btn-annulla-chiusura-${triageId}`}
            >
              Annulla
            </button>
            {isError && (
              <span className={styles.inlineMutError} data-testid={`errore-chiusura-${triageId}`}>
                {messaggioErrore(error)}
              </span>
            )}
            {erroreNote && (
              <span
                className={styles.inlineMutError}
                data-testid={`errore-chiusura-note-${triageId}`}
              >
                {ERRORE_NOTE_NON_SALVATE}
              </span>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ---------------------------------------------------------------------------
// Dialog: riapertura di un Ticket chiuso (mai promosso)
// ---------------------------------------------------------------------------

function DialogRiapri({ triageId, onChiudi }: { triageId: number; onChiudi: () => void }) {
  const [reason, setReason] = useState('');
  const { mutate, isPending, isError, error } = useReopenTicket(triageId);

  function conferma() {
    mutate(reason.trim() || undefined, { onSuccess: () => onChiudi() });
  }

  return (
    <ModalPortal onClose={onChiudi}>
      <div
        className={styles.dialogOverlay}
        role="dialog"
        aria-modal="true"
        data-testid={`dialog-riapri-${triageId}`}
      >
        <div className={styles.dialogCard}>
          <p className={styles.dialogTitle}>Riapri il ticket</p>
          <p className={styles.dialogWarning}>
            Il ticket torna tra i triage da avviare: motivazione di chiusura, presa in carico e
            collaboratori vengono azzerati, le note di analisi restano. Gli amministratori, chi lo
            aveva in carico e i collaboratori ricevono una notifica e la riapertura viene registrata
            nel log audit.
          </p>
          <div className={styles.formGroup}>
            <textarea
              className={styles.formTextarea}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo della riapertura (facoltativo)…"
              maxLength={2000}
              data-testid={`textarea-motivo-riapertura-${triageId}`}
            />
          </div>
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={conferma}
              disabled={isPending}
              data-testid={`btn-conferma-riapertura-${triageId}`}
            >
              {isPending ? 'Riapertura…' : 'Riapri ticket'}
            </button>
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={onChiudi}
              disabled={isPending}
              data-testid={`btn-annulla-riapertura-${triageId}`}
            >
              Annulla
            </button>
            {isError && (
              <span className={styles.inlineMutError} data-testid={`errore-riapertura-${triageId}`}>
                {messaggioErrore(error)}
              </span>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ---------------------------------------------------------------------------
// Badge: motivo della chiusura (sezione "Ticket chiusi")
// ---------------------------------------------------------------------------

const CLASSE_MOTIVO: Record<TipoMotivoChiusura, string | undefined> = {
  promosso: styles.badgeStateExploitable,
  non_impattato: styles.badgeStateNotAffected,
  falso_positivo: styles.badgeStateFalsePositive,
  risolto: styles.badgeStateResolved,
};

function BadgeMotivoChiusura({ triage }: { triage: Ticket }) {
  const motivo = motivoChiusura(triage);
  const tooltip =
    motivo.tipo === 'promosso'
      ? motivo.reportId !== undefined
        ? `Promosso al report ENISA #${motivo.reportId}`
        : undefined
      : triage.closedReason || undefined;
  const badge = (
    <span
      className={`${styles.badge} ${CLASSE_MOTIVO[motivo.tipo] ?? ''}`}
      title={tooltip}
      data-testid={`motivo-chiusura-${triage.id}`}
      data-motivo={motivo.tipo}
    >
      {motivo.etichetta}
    </span>
  );
  if (motivo.reportId === undefined) return badge;
  return (
    <Link
      to={enisaReportPath(motivo.reportId)}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      data-testid={`link-report-ticket-${triage.id}`}
    >
      {badge}
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Gestione collaboratori (solo lead)
// ---------------------------------------------------------------------------

function GestioneCollaboratori({ triage, isLead }: { triage: Ticket; isLead: boolean }) {
  const [nuovo, setNuovo] = useState('');
  const add = useAddCollaborator(triage.id);
  const remove = useRemoveCollaborator(triage.id);

  function aggiungi(e: React.FormEvent) {
    e.preventDefault();
    if (!nuovo.trim()) return;
    add.mutate(nuovo.trim(), { onSuccess: () => setNuovo('') });
  }

  return (
    <div className={styles.formCard} data-testid={`collaboratori-${triage.id}`}>
      <p className={styles.formTitle}>In carico e collaboratori</p>
      <p style={{ fontSize: '0.85rem', margin: '0 0 0.5rem' }}>
        In carico a:{' '}
        <strong data-testid={`triage-lead-${triage.id}`}>{triage.triageLead ?? '—'}</strong>
      </p>
      <div className={styles.bomChips}>
        {triage.collaborators.length === 0 ? (
          <span style={{ color: '#adb5bd', fontSize: '0.85rem' }}>Nessun collaboratore.</span>
        ) : (
          triage.collaborators.map((c) => (
            <span key={c} className={styles.bomChip}>
              {c}
              {isLead && (
                <button
                  type="button"
                  aria-label={`Rimuovi ${c}`}
                  className={styles.btnSmall}
                  style={{
                    marginLeft: '0.4rem',
                    border: 'none',
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  onClick={() => remove.mutate(c)}
                  disabled={remove.isPending}
                  data-testid={`btn-rimuovi-collaboratore-${triage.id}-${c}`}
                >
                  ✕
                </button>
              )}
            </span>
          ))
        )}
      </div>
      {isLead && (
        <form onSubmit={aggiungi} className={styles.formActions} style={{ marginTop: '0.6rem' }}>
          <input
            type="text"
            value={nuovo}
            onChange={(e) => setNuovo(e.target.value)}
            placeholder="email collaboratore"
            className={styles.formSelect}
            style={{ minWidth: '16rem' }}
            data-testid={`input-collaboratore-${triage.id}`}
          />
          <button
            type="submit"
            className={styles.btnSecondary}
            disabled={add.isPending || !nuovo.trim()}
            data-testid={`btn-aggiungi-collaboratore-${triage.id}`}
          >
            {add.isPending ? 'Aggiunta…' : 'Aggiungi'}
          </button>
          {add.isError && (
            <span className={styles.inlineMutError}>{messaggioErrore(add.error)}</span>
          )}
        </form>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Tabella CVE (stato VEX editabile se under-triage e abilitato)
// ---------------------------------------------------------------------------

function TabellaCve({
  triageId,
  cves,
  editabile,
  rimovibili = false,
  messaggioVuoto = 'Nessuna CVE aperta nello snapshot.',
}: {
  triageId: number;
  cves: TicketCve[];
  editabile: boolean;
  /** Mostra il pulsante di rimozione (solo Ticket manuali). */
  rimovibili?: boolean;
  messaggioVuoto?: string;
}) {
  const updateCve = useUpdateCveState(triageId);
  const rimuovi = useUnassignTicketCve(triageId);

  if (cves.length === 0) {
    return (
      <div className={styles.emptyState} data-testid="cve-triage-vuote">
        {messaggioVuoto}
      </div>
    );
  }
  return (
    <table className={styles.table} data-testid="tabella-cve-triage">
      <thead>
        <tr>
          <th>CVE ID</th>
          <th>Severità</th>
          <th>KEV</th>
          <th>Componente</th>
          <th>Stato VEX</th>
          {rimovibili && <th />}
        </tr>
      </thead>
      <tbody>
        {ordinaCve(cves).map((c) => (
          <tr key={c.cveId} data-testid={`riga-cve-triage-${c.cveId}`}>
            <td style={{ whiteSpace: 'nowrap' }}>
              <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{c.cveId}</span>
            </td>
            <td>
              <span className={`${styles.badge} ${classeSeverita(c.severity)}`}>
                {c.severity ?? 'N/D'}
              </span>
            </td>
            <td>
              {c.knownExploited ? (
                <span className={`${styles.badge} ${styles.badgeStateExploitable ?? ''}`}>KEV</span>
              ) : (
                <span style={{ color: '#adb5bd' }}>—</span>
              )}
            </td>
            <td>
              {c.componentName ? (
                <>
                  <span style={{ fontWeight: 500 }}>{c.componentName}</span>
                  {c.componentVersion && (
                    <span style={{ color: '#6c757d', marginLeft: '0.4rem', fontSize: '0.82rem' }}>
                      {c.componentVersion}
                    </span>
                  )}
                </>
              ) : (
                <span style={{ color: '#adb5bd' }}>—</span>
              )}
            </td>
            <td>
              {editabile ? (
                <select
                  className={styles.formSelect}
                  value={c.analysisState ?? 'NOT_SET'}
                  disabled={updateCve.isPending}
                  onChange={(e) =>
                    updateCve.mutate({ cveId: c.cveId, analysisState: e.target.value })
                  }
                  data-testid={`select-vex-${triageId}-${c.cveId}`}
                >
                  {STATI_VEX_TRIAGE.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              ) : (
                <span style={{ fontSize: '0.82rem', color: '#6c757d' }}>
                  {c.analysisState ?? 'NOT_SET'}
                </span>
              )}
            </td>
            {rimovibili && (
              <td style={{ textAlign: 'right' }}>
                <button
                  type="button"
                  className={styles.btnSecondary}
                  style={{ padding: '0.2rem 0.5rem', fontSize: '0.78rem' }}
                  onClick={() => rimuovi.mutate(c.cveId)}
                  disabled={rimuovi.isPending}
                  aria-label={`Rimuovi ${c.cveId} dal ticket`}
                  data-testid={`btn-rimuovi-cve-${triageId}-${c.cveId}`}
                >
                  Rimuovi
                </button>
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ---------------------------------------------------------------------------
// Pannello dettaglio Ticket
// ---------------------------------------------------------------------------

/**
 * Riga discreta di testata: chi ha creato il ticket e quando. I ticket automatici non hanno un
 * utente: li crea il job automatico, e si dice quello. Quelli manuali precedenti al campo
 * `createdBy` mostrano solo la data.
 */
/**
 * Ticket promosso: link alle pagine dei report ENISA generati (uno per release del ticket), in
 * testa al dettaglio. Speculare al link "Ticket di origine" della pagina /enisa-reports.
 */
function ReportEnisaDelTicket({ triage }: { triage: Ticket }) {
  const motivo = motivoChiusura(triage);
  if (motivo.tipo !== 'promosso') return null;
  const ids = motivo.reportIds ?? [];
  return (
    <p className={styles.reportPromosso} data-testid={`report-enisa-ticket-${triage.id}`}>
      <span className={styles.reportPromossoEtichetta}>Promosso a report ENISA:</span>{' '}
      {ids.length === 0
        ? 'report non registrato.'
        : ids.map((id, i) => (
            <Fragment key={id}>
              {i > 0 && ', '}
              <Link
                to={enisaReportPath(id)}
                className={styles.reportPromossoLink}
                data-testid={`link-report-dettaglio-${triage.id}-${id}`}
              >
                Report ENISA #{id}
              </Link>
            </Fragment>
          ))}
    </p>
  );
}

function CreazioneTicket({ triage }: { triage: Ticket }) {
  const quando = triage.createdAt ? formattaDataOra(triage.createdAt) : undefined;
  const automatico = triage.origin === 'auto';
  const creatore = triage.payload.createdBy;
  return (
    <p className={styles.creazione} data-testid={`creazione-ticket-${triage.id}`}>
      {automatico ? (
        <span className={styles.creazioneJob} tabIndex={0}>
          <svg
            className={styles.creazioneIcona}
            width="12"
            height="12"
            viewBox="0 0 12 12"
            aria-hidden="true"
          >
            <circle cx="6" cy="6" r="1.8" fill="none" stroke="currentColor" strokeWidth="1.3" />
            <circle
              cx="6"
              cy="6"
              r="4.6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeDasharray="1.6 1.4"
            />
          </svg>
          Creato dal <span className={styles.creazioneJobNome}>job automatico</span>
          <span className={styles.creazioneTooltip} role="tooltip">
            Job Prefect «product-triage-autocreate»
          </span>
        </span>
      ) : creatore ? (
        <>
          Creato da <strong>{creatore}</strong>
        </>
      ) : (
        'Creato'
      )}
      {quando && <> · {quando}</>}
    </p>
  );
}

// ---------------------------------------------------------------------------
// Nota per il cliente (chiusura "non impattato" di un Ticket manuale)
// ---------------------------------------------------------------------------

/** Id del contatore di parole: descrive sia la textarea sia il pulsante "Chiudi". */
function idContaParole(triageId: number): string {
  return `conta-parole-nota-cliente-${triageId}`;
}

/**
 * Il segnalante non ha un indirizzo email noto al portale: né contatto libero con email né
 * contatto dell'anagrafica clienti (che ha sempre un'email). La nota resta solo sul ticket.
 */
function segnalanteSenzaEmail(triage: Ticket): boolean {
  const r = triage.payload.source?.reporter;
  return !r || (!r.email && r.contactId === undefined);
}

function NotaPerIlCliente({
  triage,
  valore,
  onChange,
  minParole,
  disabled,
}: {
  triage: Ticket;
  valore: string;
  onChange: (v: string) => void;
  minParole: number;
  disabled: boolean;
}) {
  const id = triage.id;
  const parole = countWords(valore);
  const sufficiente = parole >= minParole;
  const idTextarea = `textarea-nota-cliente-${id}`;
  return (
    <div className={styles.notaCliente}>
      <label className={styles.formGroupLabel} htmlFor={idTextarea}>
        Note per il cliente
      </label>
      <textarea
        id={idTextarea}
        className={`${styles.formTextarea} ${styles.notaClienteTextarea}`}
        value={valore}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        required
        aria-required="true"
        aria-invalid={valore.trim() !== '' && !sufficiente ? true : undefined}
        aria-describedby={idContaParole(id)}
        placeholder={`Comunicazione per chi ha segnalato il ticket (almeno ${minParole} parole)…`}
        data-testid={idTextarea}
      />
      <span
        id={idContaParole(id)}
        className={`${styles.contaParole} ${sufficiente ? styles.contaParoleOk : ''}`}
        data-testid={idContaParole(id)}
      >
        {parole} / {minParole} parole
      </span>
      {!sufficiente && (
        <span className={styles.contaParole} data-testid={`motivo-chiudi-disabilitato-${id}`}>
          «Chiudi» si abilita quando la nota arriva ad almeno {minParole} parole.
        </span>
      )}
      {segnalanteSenzaEmail(triage) && (
        <span className={styles.contaParole} data-testid={`avviso-nota-senza-email-${id}`}>
          Il segnalante non ha un indirizzo email: la nota resterà sul ticket ma non potrà essere
          inviata.
        </span>
      )}
    </div>
  );
}

function PannelloDettaglioTriage({
  triageId,
  puoAnalizzare,
  attoreCorrente,
}: {
  triageId: number;
  puoAnalizzare: boolean;
  attoreCorrente: string | undefined;
}) {
  const { data: triage, isLoading, isError, error } = useTicket(triageId);
  const [dialogPromuovi, setDialogPromuovi] = useState<ReportCategory | null>(null);
  const [dialogChiudi, setDialogChiudi] = useState(false);
  const [dialogRiapri, setDialogRiapri] = useState(false);
  const [dialogAzioni, setDialogAzioni] = useState(false);
  // Nota per il cliente (solo ticket manuali): sopravvive alla chiusura del dialog delle azioni,
  // così il passaggio al dialog di conferma (o un "Annulla") non la perde.
  const [notaCliente, setNotaCliente] = useState('');
  const notaRichiesta = triage?.origin === 'manual';
  const { data: impostazioniNota } = useCustomerNoteSettings(notaRichiesta && dialogAzioni);
  const start = useStartTicket(triageId);
  const leave = useLeaveTicket(triageId);
  // Bozza delle note: salvata prima di ogni cambio di stato, così non si perde mai.
  const note = useNoteAnalista(triage, triageId);
  const [erroreNoteAzione, setErroreNoteAzione] = useState(false);

  /** Esegue un'azione di workflow dopo aver salvato le note modificate (se il salvataggio riesce). */
  async function dopoAverSalvatoLeNote(azione: () => void) {
    setErroreNoteAzione(false);
    if (await note.salvaSeModificate()) azione();
    else setErroreNoteAzione(true);
  }

  if (isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid={`dettaglio-triage-loading-${triageId}`}>
        Caricamento dettaglio…
      </p>
    );
  }
  if (isError || !triage) {
    return (
      <div className={styles.errorMsg} data-testid={`dettaglio-triage-error-${triageId}`}>
        {messaggioErrore(error)}
      </div>
    );
  }

  const { payload } = triage;
  const inTriage = triage.status === 'in_triage';
  const chiuso = triage.status === 'closed';
  const promosso = triage.status === 'promoted';
  const attore = normalizzaAttore(attoreCorrente);
  const isLead = inTriage && normalizzaAttore(triage.triageLead) === attore && attore !== '';
  const isCollaboratore = triage.collaborators.some(
    (c) => normalizzaAttore(c) === attore && attore !== '',
  );
  const puoEditare = puoAnalizzare && inTriage && (isLead || isCollaboratore);
  const manuale = triage.origin === 'manual';
  // Ticket manuale: "Chiudi" resta disabilitato finché la nota per il cliente non ha almeno
  // `minParoleNota` parole (parole >= minimo); il server applica la stessa regola (422).
  const minParoleNota = impostazioniNota?.customerNoteMinWords ?? DEFAULT_CUSTOMER_NOTE_MIN_WORDS;
  const notaSufficiente = !manuale || countWords(notaCliente) >= minParoleNota;
  // CVE assegnabili a mano solo sui Ticket manuali: liberamente se non in carico, altrimenti
  // solo dal lead/collaboratore (stesse regole del server).
  const puoAssegnareCve = puoAnalizzare && manuale && (triage.status === 'open' || puoEditare);

  const clientiInCampo = payload.impactedCustomers.map((c) => c.customerName).join(', ');
  const clientiRiga = clientiInCampo && (
    <>
      <dt className={styles.campoEtichetta}>Clienti in campo</dt>
      <dd className={styles.campoValore} data-testid={`clienti-ticket-${triageId}`}>
        {clientiInCampo}
      </dd>
    </>
  );
  const descrizione = manuale ? payload.description : undefined;

  return (
    <div className={styles.dettaglio} data-testid={`dettaglio-triage-${triageId}`}>
      {/* Testata: descrizione e chi/come ha creato il ticket */}
      <div className={styles.dettaglioTesta}>
        {descrizione && (
          <p className={styles.dettaglioDescrizione} data-testid={`descrizione-ticket-${triageId}`}>
            {descrizione}
          </p>
        )}
        <CreazioneTicket triage={triage} />
        <ReportEnisaDelTicket triage={triage} />
      </div>

      {/* Azioni — in cima, prima delle schede e dell'elenco CVE */}
      {puoAnalizzare && (
        <div className={styles.dettaglioAzioni}>
          {triage.status === 'open' && (
            <button
              type="button"
              className={styles.btnPrimary}
              onClick={() => void dopoAverSalvatoLeNote(() => start.mutate())}
              disabled={start.isPending}
              data-testid={`btn-avvia-triage-${triageId}`}
            >
              {start.isPending ? 'Presa in carico…' : 'Prendi in carico'}
            </button>
          )}

          {inTriage && (
            <>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => setDialogPromuovi('actively_exploited_vulnerability')}
                data-testid={`btn-promuovi-aev-${triageId}`}
              >
                <span aria-hidden="true">→</span> Actively Exploited Vulnerability
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={() => setDialogPromuovi('severe_incident')}
                data-testid={`btn-promuovi-incident-${triageId}`}
              >
                <span aria-hidden="true">→</span> Severe Incident
              </button>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={() => setDialogAzioni(true)}
                aria-label="Chiudi o abbandona il triage"
                title="Chiudi o abbandona il triage"
                data-testid={`btn-azioni-chiusura-${triageId}`}
              >
                <span aria-hidden="true">✕</span>
              </button>
            </>
          )}
          {isRiapribile(triage) && (
            <button
              type="button"
              className={styles.btnSecondary}
              onClick={() => setDialogRiapri(true)}
              title="Riporta il ticket tra i triage da avviare"
              data-testid={`btn-riapri-ticket-${triageId}`}
            >
              Riapri
            </button>
          )}
          {start.isError && (
            <span className={styles.inlineMutError}>{messaggioErrore(start.error)}</span>
          )}
          {leave.isError && (
            <span className={styles.inlineMutError}>{messaggioErrore(leave.error)}</span>
          )}
          {erroreNoteAzione && (
            <span className={styles.inlineMutError} data-testid={`errore-azione-note-${triageId}`}>
              {ERRORE_NOTE_NON_SALVATE}
            </span>
          )}
        </div>
      )}

      {/* Schede informative */}
      <div className={styles.dettaglioGriglia}>
        {manuale && payload.source && (
          <section className={styles.scheda}>
            <h3 className={styles.schedaTitolo}>Origine</h3>
            <SorgenteDettaglio ticketId={triageId} source={payload.source} />
          </section>
        )}

        <section className={styles.scheda}>
          {manuale ? (
            <>
              <h3 className={styles.schedaTitolo}>Release coinvolte</h3>
              <dl className={styles.campi} data-testid={`release-ticket-${triageId}`}>
                {versioniPerProdotto(triage.releases).map((g) => (
                  <Fragment key={g.productInternalId}>
                    <dt className={styles.campoEtichetta}>{g.productName}</dt>
                    <dd className={styles.campoValore}>
                      <span className={styles.versioni}>
                        {g.versioni.map((v) => (
                          <span key={v} className={styles.versione}>
                            {v}
                          </span>
                        ))}
                      </span>
                    </dd>
                  </Fragment>
                ))}
                {clientiRiga}
              </dl>
            </>
          ) : (
            <>
              {/* SBOM (solo Ticket automatici: derivano dagli snapshot della release) */}
              <h3 className={styles.schedaTitolo}>SBOM</h3>
              {payload.bomSummary.length > 0 ? (
                <div className={styles.bomChips} data-testid={`sbom-triage-${triageId}`}>
                  {payload.bomSummary.map((b) => (
                    <span key={b.name} className={styles.bomChip}>
                      {b.name}
                      {b.version ? ` ${b.version}` : ''}
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{ color: '#adb5bd' }}>Nessun *BOM associato.</span>
              )}
              {clientiRiga && (
                <dl className={styles.campi} style={{ marginTop: '0.75rem' }}>
                  {clientiRiga}
                </dl>
              )}
            </>
          )}
        </section>
      </div>

      {/* Dettaglio CVE */}
      <section className={styles.dettaglioCve}>
        <h3 className={styles.sezioneTitolo}>
          {manuale ? 'CVE assegnate' : 'CVE aperte'}{' '}
          <span className={styles.sezioneConteggio}>({payload.cves.length})</span>
        </h3>
        {manuale && puoAssegnareCve && payload.cves.length === 0 ? (
          <div className={styles.statoVuoto} data-testid="cve-triage-vuote">
            <p className={styles.statoVuotoTitolo}>Nessuna CVE assegnata</p>
            <p className={styles.statoVuotoTesto}>
              Cerca una CVE nota per assegnarla a questo ticket.
            </p>
            <CvePicker ticketId={triageId} giaAssegnate={[]} inline />
          </div>
        ) : (
          <>
            <TabellaCve
              triageId={triageId}
              cves={payload.cves}
              editabile={puoEditare}
              rimovibili={puoAssegnareCve}
              messaggioVuoto={
                manuale ? 'Nessuna CVE assegnata.' : 'Nessuna CVE aperta nello snapshot.'
              }
            />
            {puoAssegnareCve && (
              <CvePicker ticketId={triageId} giaAssegnate={payload.cves.map((c) => c.cveId)} />
            )}
          </>
        )}
      </section>

      {/* In carico a + collaboratori (solo se in carico) */}
      {inTriage && puoAnalizzare && <GestioneCollaboratori triage={triage} isLead={isLead} />}

      {/* Chiusura (solo Ticket chiusi) */}
      {chiuso && (
        <p
          style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', marginTop: '0.8rem' }}
          data-testid={`chiusura-ticket-${triageId}`}
        >
          <strong>
            Chiuso{triage.closedAt ? ` il ${new Date(triage.closedAt).toLocaleDateString()}` : ''}:
          </strong>{' '}
          {triage.closedReason || '—'}
        </p>
      )}
      {chiuso && triage.customerNote && (
        <p
          style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', marginTop: '0.4rem' }}
          data-testid={`nota-cliente-ticket-${triageId}`}
        >
          <strong>Nota per il cliente (inviata a chi ha segnalato):</strong> {triage.customerNote}
        </p>
      )}

      {/* Note analista (sola lettura sui Ticket chiusi e promossi: sui promossi sono sul report) */}
      {puoAnalizzare && !chiuso && !promosso ? (
        <NoteAnalistaForm triage={triage} note={note} />
      ) : (
        payload.analystNotes && (
          <p
            style={{ fontSize: '0.85rem', whiteSpace: 'pre-wrap', marginTop: '0.8rem' }}
            data-testid={`note-sola-lettura-${triageId}`}
          >
            <strong>Note di analisi{promosso ? ' (riportate sul report ENISA)' : ''}:</strong>{' '}
            {payload.analystNotes}
          </p>
        )
      )}

      {dialogPromuovi && (
        <DialogPromuovi
          triageId={triageId}
          numRelease={triage.releases.length}
          numCve={triage.payload.cves.length}
          category={dialogPromuovi}
          salvaNote={note.salvaSeModificate}
          onChiudi={() => setDialogPromuovi(null)}
        />
      )}
      {dialogAzioni && (
        <ModalPortal onClose={() => setDialogAzioni(false)}>
          <div
            className={styles.dialogOverlay}
            role="dialog"
            aria-modal="true"
            data-testid={`dialog-azioni-chiusura-${triageId}`}
          >
            <div className={`${styles.dialogCard} ${styles.dialogCardAzioni}`}>
              <p className={styles.dialogTitle}>Chiudi o abbandona il triage</p>
              <div className={styles.azioneOpzione}>
                <p
                  className={styles.azioneDescrizione}
                  data-testid={`descrizione-chiudi-triage-${triageId}`}
                >
                  {manuale
                    ? 'Il tuo prodotto verrà marcato come non impattato e la seguente comunicazione verrà inviata a chi ha segnalato il ticket.'
                    : 'Il tuo prodotto verrà marcato come "non impattato".'}
                </p>
                {manuale && (
                  <NotaPerIlCliente
                    triage={triage}
                    valore={notaCliente}
                    onChange={setNotaCliente}
                    minParole={
                      impostazioniNota?.customerNoteMinWords ?? DEFAULT_CUSTOMER_NOTE_MIN_WORDS
                    }
                    disabled={!puoEditare}
                  />
                )}
                <button
                  type="button"
                  className={styles.btnSecondary}
                  onClick={() => {
                    setDialogAzioni(false);
                    setDialogChiudi(true);
                  }}
                  disabled={!puoEditare || !notaSufficiente}
                  aria-disabled={!puoEditare || !notaSufficiente}
                  aria-describedby={manuale ? idContaParole(triageId) : undefined}
                  title={
                    !puoEditare
                      ? 'Solo chi ha in carico il ticket o un collaboratore'
                      : !notaSufficiente
                        ? `Scrivi la nota per il cliente: almeno ${minParoleNota} parole`
                        : undefined
                  }
                  data-testid={`btn-chiudi-triage-${triageId}`}
                >
                  Chiudi
                </button>
              </div>
              {isLead && (
                <>
                  <hr className={styles.azioneSeparatore} />
                  <div className={styles.azioneOpzione}>
                    <p
                      className={styles.azioneDescrizione}
                      data-testid={`descrizione-abbandona-triage-${triageId}`}
                    >
                      Il ticket non sarà più assegnato a te e l'admin verrà notificato del
                      passaggio.
                    </p>
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => {
                        setDialogAzioni(false);
                        void dopoAverSalvatoLeNote(() => leave.mutate());
                      }}
                      disabled={leave.isPending}
                      data-testid={`btn-abbandona-triage-${triageId}`}
                    >
                      {leave.isPending ? 'Abbandono…' : 'Abbandona'}
                    </button>
                  </div>
                </>
              )}
              <div className={styles.azioniFooter}>
                <button
                  type="button"
                  className={styles.btnDanger}
                  onClick={() => setDialogAzioni(false)}
                  data-testid={`btn-annulla-azioni-${triageId}`}
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
      {dialogChiudi && (
        <DialogChiudi
          triageId={triageId}
          notaCliente={manuale ? notaCliente : undefined}
          salvaNote={note.salvaSeModificate}
          onChiudi={() => setDialogChiudi(false)}
          onChiuso={() => setNotaCliente('')}
        />
      )}
      {dialogRiapri && <DialogRiapri triageId={triageId} onChiudi={() => setDialogRiapri(false)} />}

      {/* Deep link al ticket (in basso a destra): rivela l'URL condivisibile + copia */}
      <LinkTicket triageId={triageId} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Deep link al ticket (icona link → URL + copia)
// ---------------------------------------------------------------------------

/** Icona "link" (anelli di catena). */
function IconaLink() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <path
        d="M6.6 9.4a2.6 2.6 0 0 0 3.7 0l2.6-2.6a2.6 2.6 0 0 0-3.7-3.7l-.9.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
      <path
        d="M9.4 6.6a2.6 2.6 0 0 0-3.7 0L3.1 9.2a2.6 2.6 0 0 0 3.7 3.7l.9-.9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

/**
 * Link diretto al ticket: l'icona rivela l'URL che apre /tickets con QUESTO ticket espanso
 * (`?ticket=<id>#by-product`) e il bottone per copiarlo negli appunti.
 */
function LinkTicket({ triageId }: { triageId: number }) {
  const [aperto, setAperto] = useState(false);
  const url = ticketUrl(triageId, window.location.origin);
  return (
    <div className={styles.linkTicket} data-testid={`link-ticket-${triageId}`}>
      {aperto && (
        <span className={styles.linkTicketUrl}>
          <input
            type="text"
            readOnly
            value={url}
            className={styles.linkTicketInput}
            aria-label="Link diretto al ticket"
            onFocus={(e) => e.currentTarget.select()}
            onClick={(e) => e.stopPropagation()}
            data-testid={`url-ticket-${triageId}`}
          />
          <CopyButton text={url} label="Copia link" testId={`btn-copia-link-ticket-${triageId}`} />
        </span>
      )}
      <button
        type="button"
        className={styles.linkTicketBtn}
        onClick={(e) => {
          e.stopPropagation();
          setAperto((v) => !v);
        }}
        aria-expanded={aperto}
        aria-label="Link diretto al ticket"
        title="Link diretto al ticket"
        data-testid={`btn-link-ticket-${triageId}`}
      >
        <IconaLink />
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Riga Ticket (disclosure) — mostra i fattori di rischio
// ---------------------------------------------------------------------------

function RigaTicket({
  triage,
  prodotto,
  puoAnalizzare,
  attoreCorrente,
  aperta,
  onToggle,
  mostraMotivo = false,
}: {
  triage: Ticket;
  prodotto: string;
  puoAnalizzare: boolean;
  attoreCorrente: string | undefined;
  aperta: boolean;
  onToggle: () => void;
  /** Colonna "Motivo chiusura" (solo sezione "Ticket chiusi"). */
  mostraMotivo?: boolean;
}) {
  const kev = contaKev(triage);
  return (
    <>
      <tr
        className={styles.cveRowClickable}
        role="button"
        tabIndex={0}
        aria-expanded={aperta}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        data-testid={`riga-ticket-${triage.id}`}
      >
        <td style={{ fontWeight: 600, fontSize: '0.88rem' }}>
          <span className={styles.cellaTitolo}>
            <svg
              className={styles.chevron}
              data-aperto={aperta}
              width="10"
              height="10"
              viewBox="0 0 10 10"
              aria-hidden="true"
            >
              <path
                d="M3 1.5 6.5 5 3 8.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>
              {triage.origin === 'manual' ? (
                <>
                  {triage.payload.title}
                  <span className={styles.badgeManual}>manuale</span>
                </>
              ) : (
                prodotto
              )}
            </span>
          </span>
        </td>
        <td style={{ whiteSpace: 'nowrap' }}>
          {triage.origin === 'manual' ? (
            <span className={styles.releaseSummary}>
              {riepilogoRelease(triage).map((riga) => (
                <span key={riga}>{riga}</span>
              ))}
            </span>
          ) : (
            <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {triage.releaseVersion}
            </span>
          )}
        </td>
        <td data-testid={`riga-kev-${triage.id}`} style={{ whiteSpace: 'nowrap' }}>
          {kev > 0 ? (
            <span className={`${styles.badge} ${styles.badgeStateExploitable ?? ''}`}>
              {kev} KEV
            </span>
          ) : (
            <span style={{ color: '#adb5bd' }}>—</span>
          )}
        </td>
        <td>
          <span className={`${styles.badge} ${classeSeverita(etichettaSeveritaMassima(triage))}`}>
            {etichettaSeveritaMassima(triage)}
          </span>
        </td>
        <td data-testid={`riga-clienti-${triage.id}`}>{contaClienti(triage)}</td>
        <td>{triage.payload.cves.length}</td>
        <td>
          {triage.triageLead ? (
            <span style={{ fontSize: '0.82rem' }}>{triage.triageLead}</span>
          ) : (
            <span style={{ color: '#adb5bd' }}>—</span>
          )}
        </td>
        {mostraMotivo && (
          <td style={{ whiteSpace: 'nowrap' }}>
            <BadgeMotivoChiusura triage={triage} />
          </td>
        )}
        <td>
          <span className={styles.srOnly}>{aperta ? 'Comprimi' : 'Espandi'}</span>
        </td>
      </tr>
      {aperta && (
        <tr data-testid={`riga-ticket-espansa-${triage.id}`}>
          <td colSpan={mostraMotivo ? 9 : 8} style={{ padding: 0 }}>
            <PannelloDettaglioTriage
              triageId={triage.id}
              puoAnalizzare={puoAnalizzare}
              attoreCorrente={attoreCorrente}
            />
            {/* Storico modifiche del ticket (log audit), chiuso di default */}
            <div className={styles.storicoTicket}>
              <TicketAuditTrail ticket={triage} />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Sezione (gruppi per prodotto)
// ---------------------------------------------------------------------------

function SezioneTriage({
  titolo,
  descrizione,
  testid,
  triages,
  puoAnalizzare,
  attoreCorrente,
  espansi,
  onToggle,
  messaggioVuoto,
  categoriaDi,
  tabellaTestid,
  mostraMotivo = false,
}: {
  titolo: string;
  descrizione: string;
  testid: string;
  triages: Ticket[];
  puoAnalizzare: boolean;
  attoreCorrente: string | undefined;
  espansi: Set<number>;
  onToggle: (id: number) => void;
  messaggioVuoto: string;
  categoriaDi?: (t: Ticket) => CraCategory | undefined;
  tabellaTestid?: string;
  /** Aggiunge la colonna "Motivo chiusura" (sezione "Ticket chiusi"). */
  mostraMotivo?: boolean;
}) {
  const gruppi = raggruppaPerProdotto(triages, categoriaDi);
  return (
    <section
      data-testid={testid}
      style={{
        marginBottom: '2rem',
        borderRadius: '8px',
        padding: '1rem',
        background:
          testid === 'sezione-da-avviare'
            ? '#fff8e6'
            : testid === 'sezione-chiusi'
              ? '#f1f3f5'
              : '#e8f4fd',
        // Filo di colore che lega il pannello di dettaglio alla sezione.
        ['--sezione-accento' as string]:
          testid === 'sezione-da-avviare'
            ? '#e0a800'
            : testid === 'sezione-chiusi'
              ? '#868e96'
              : '#0b5cad',
      }}
    >
      <h2 className={styles.panelTitle} style={{ fontSize: '1.05rem' }}>
        {titolo} <span style={{ color: '#6c757d', fontWeight: 400 }}>({triages.length})</span>
      </h2>
      <p style={{ fontSize: '0.85rem', color: '#6c757d', margin: '0 0 0.8rem' }}>{descrizione}</p>
      {triages.length === 0 ? (
        <div className={styles.emptyState} data-testid={`${testid}-vuoto`}>
          {messaggioVuoto}
        </div>
      ) : (
        <table className={styles.table} data-testid={tabellaTestid}>
          <thead>
            <tr>
              <th>Prodotto</th>
              <th>Release</th>
              <th>KEV</th>
              <th>Severità max</th>
              <th>Clienti</th>
              <th>CVE</th>
              <th>In carico a</th>
              {mostraMotivo && <th>Motivo chiusura</th>}
              <th />
            </tr>
          </thead>
          <tbody>
            {gruppi.flatMap(([prodotto, righe]) =>
              righe.map((t) => (
                <RigaTicket
                  key={t.id}
                  triage={t}
                  prodotto={prodotto}
                  puoAnalizzare={puoAnalizzare}
                  attoreCorrente={attoreCorrente}
                  aperta={espansi.has(t.id)}
                  onToggle={() => onToggle(t.id)}
                  mostraMotivo={mostraMotivo}
                />
              )),
            )}
          </tbody>
        </table>
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Lista Ticket (vista #by-product) — due sezioni
// ---------------------------------------------------------------------------

export function TicketList() {
  const { can } = usePermissions();
  const { claims } = useAuth();
  const puoAnalizzare = can('VULNERABILITY_ANALYSIS');

  // Identità corrente, allineata alla logica server (name → email → sub).
  const attoreCorrente = claims
    ? ((claims['name'] as string | undefined) ??
      (claims['email'] as string | undefined) ??
      claims.sub)
    : undefined;

  // Deep link /tickets?ticket=<id>: riga espansa (e sezione "chiusi" aperta se serve) + scroll.
  const [searchParams] = useSearchParams();
  const ticketLinkato = idTicketDaParametro(searchParams.get(TICKET_PARAM));
  const { data: dettaglioLinkato } = useTicket(ticketLinkato);

  const nonAvviati = useTickets('open');
  const inCorso = useTickets('in_triage');
  // Ticket chiusi nascosti di default: caricati solo quando l'utente li mostra.
  const [mostraChiusi, setMostraChiusi] = useState(false);
  const chiusi = useTickets('closed', { enabled: mostraChiusi });
  const promossi = useTickets('promoted', { enabled: mostraChiusi });
  const { data: prodotti } = useProducts();

  // Mappa prodotto → categoria CRA per ordinamento per urgenza.
  const categorieLookup = new Map<string, CraCategory | undefined>();
  if (prodotti) {
    for (const p of prodotti) {
      categorieLookup.set(p.internalId, p.craCategory);
    }
  }

  // Categoria CRA di un triage: per i Ticket multi-release la più urgente tra i prodotti coinvolti.
  const categoriaDi = (triage: Ticket): CraCategory | undefined => {
    let migliore: CraCategory | undefined;
    for (const r of triage.releases) {
      const cat = categorieLookup.get(r.productInternalId);
      if (rangoCategoriaCra(cat) > rangoCategoriaCra(migliore)) migliore = cat;
    }
    return migliore ?? categorieLookup.get(triage.productInternalId ?? '');
  };
  const [dialogNuovo, setDialogNuovo] = useState(false);

  // Espansione condivisa tra le sezioni (per id): un triage avviato resta aperto migrando.
  const [espansi, setEspansi] = useState<Set<number>>(
    () => new Set(ticketLinkato !== undefined ? [ticketLinkato] : []),
  );

  // Link a un altro ticket mentre la pagina è già aperta: lo espande senza chiudere gli altri.
  useEffect(() => {
    if (ticketLinkato === undefined) return;
    setEspansi((prev) => (prev.has(ticketLinkato) ? prev : new Set(prev).add(ticketLinkato)));
  }, [ticketLinkato]);

  // Il ticket linkato è chiuso/promosso → la sua riga vive nella sezione "Ticket chiusi".
  const linkatoChiuso =
    dettaglioLinkato?.status === 'closed' || dettaglioLinkato?.status === 'promoted';
  useEffect(() => {
    if (linkatoChiuso) setMostraChiusi(true);
  }, [linkatoChiuso]);

  // Porta in vista la riga linkata appena compare (una volta per ticket).
  const scrollFatto = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (ticketLinkato === undefined || scrollFatto.current === ticketLinkato) return;
    const riga = document.querySelector(`[data-testid="riga-ticket-${ticketLinkato}"]`);
    if (!riga) return;
    scrollFatto.current = ticketLinkato;
    riga.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
  });
  const toggle = (id: number) =>
    setEspansi((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  if (nonAvviati.isLoading || inCorso.isLoading) {
    return (
      <p className={styles.loadingMsg} data-testid="ticket-loading">
        Caricamento Ticket…
      </p>
    );
  }
  if (nonAvviati.isError || inCorso.isError) {
    return (
      <div className={styles.errorMsg} data-testid="ticket-error">
        {messaggioErrore(nonAvviati.error ?? inCorso.error)}
      </div>
    );
  }

  return (
    <div data-testid="ticket-sezioni">
      {puoAnalizzare && (
        <div className={styles.toolbar} style={{ justifyContent: 'flex-end' }}>
          <button
            type="button"
            className={styles.btnPrimary}
            onClick={() => setDialogNuovo(true)}
            data-testid="btn-nuovo-ticket"
          >
            + Nuovo ticket
          </button>
        </div>
      )}
      {dialogNuovo && (
        <TicketCreateDialog
          onChiudi={() => setDialogNuovo(false)}
          onCreato={(t) => setEspansi((prev) => new Set(prev).add(t.id))}
        />
      )}
      <SezioneTriage
        titolo="Triage da avviare"
        descrizione="Ticket non ancora presi in carico, dal più urgente (KEV, severità, clienti impattati). Espandi un prodotto per vedere le CVE e avviare il triage."
        testid="sezione-da-avviare"
        triages={nonAvviati.data ?? []}
        puoAnalizzare={puoAnalizzare}
        attoreCorrente={attoreCorrente}
        espansi={espansi}
        onToggle={toggle}
        messaggioVuoto="Nessun triage da avviare. Vengono creati automaticamente per le release con CVE non triagate e almeno un cliente in campo, oppure a mano con “Nuovo ticket”."
        categoriaDi={categoriaDi}
      />
      <SezioneTriage
        titolo="Triage in corso"
        descrizione="Ticket presi in carico. Chi ha in carico il ticket (o un collaboratore) può editare lo stato VEX, chiudere o promuovere a bozza SRP (Actively Exploited Vulnerability o Severe Incident)."
        testid="sezione-in-corso"
        triages={inCorso.data ?? []}
        puoAnalizzare={puoAnalizzare}
        attoreCorrente={attoreCorrente}
        espansi={espansi}
        onToggle={toggle}
        messaggioVuoto="Nessun triage in corso."
        categoriaDi={categoriaDi}
      />
      <div className={styles.toolbar} style={{ justifyContent: 'flex-end' }}>
        <button
          type="button"
          className={styles.btnSecondary}
          onClick={() => setMostraChiusi((v) => !v)}
          aria-pressed={mostraChiusi}
          data-testid="btn-toggle-chiusi"
        >
          {mostraChiusi ? 'Nascondi chiusi' : 'Mostra chiusi'}
        </button>
      </div>
      {mostraChiusi &&
        (chiusi.isLoading || promossi.isLoading ? (
          <p className={styles.loadingMsg} data-testid="ticket-chiusi-loading">
            Caricamento Ticket chiusi…
          </p>
        ) : chiusi.isError || promossi.isError ? (
          <div className={styles.errorMsg} data-testid="ticket-chiusi-error">
            {messaggioErrore(chiusi.error ?? promossi.error)}
          </div>
        ) : (
          <SezioneTriage
            titolo="Ticket chiusi"
            descrizione="Ticket chiusi senza escalation (es. prodotto non impattato) o promossi a report ENISA: il badge dice perché. I ticket chiusi e mai promossi si possono riaprire dal dettaglio."
            testid="sezione-chiusi"
            tabellaTestid="tickets-chiusi-tabella"
            mostraMotivo
            triages={[...(chiusi.data ?? []), ...(promossi.data ?? [])]}
            puoAnalizzare={puoAnalizzare}
            attoreCorrente={attoreCorrente}
            espansi={espansi}
            onToggle={toggle}
            messaggioVuoto="Nessun ticket chiuso."
            categoriaDi={categoriaDi}
          />
        ))}
    </div>
  );
}
